import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "src/store";
import { LeaveFormModal } from "../create-leave";
import { ILeaveType } from "src/slices/leaveType/types";
import { selectLeaveTypeList } from "src/slices/leaveType/selectors";
import {
  deleteLeaveType,
  fetchLeaveTypeList,
  upsertLeaveType,
} from "src/slices/leaveType/thunks";

export const LeaveTypePage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const leaveRows = useSelector(selectLeaveTypeList);

  const [openLeaveModal, setOpenLeaveModal] = useState(false);
  const [editingLeave, setEditingLeave] = useState<ILeaveType | null>(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  useEffect(() => {
    dispatch(fetchLeaveTypeList());
  }, [dispatch]);

  const leaveColumns: MRT_ColumnDef<ILeaveType>[] = [
    { accessorKey: "name", header: "Leave Name" },
    { accessorKey: "quota", header: "Quota" },
    { accessorKey: "category", header: "Category" },
    {
      accessorKey: "carryForward",
      header: "Carry Forward",
      Cell: ({ cell }) => (cell.getValue<boolean>() ? "Yes" : "No"),
    },
    { accessorKey: "effectiveFrom", header: "Effective From" },
    {
      accessorKey: "isActive",
      header: "Status",
      Cell: ({ cell }) => (cell.getValue<boolean>() ? "Active" : "Inactive"),
    },
  ];

  const handleSubmitLeave = async (data: any) => {
    if (editingLeave) {
      await dispatch(upsertLeaveType({ ...data, id: editingLeave.id }));
    } else {
      await dispatch(upsertLeaveType(data));
    }
    dispatch(fetchLeaveTypeList());
  };

  const handleDelete = async () => {
    await dispatch(deleteLeaveType(selectedRecord.id));
    dispatch(fetchLeaveTypeList());
    setOpenConfirm(false);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <MaterialReactTable
        columns={leaveColumns}
        data={leaveRows || []}
        enableRowActions
        renderRowActionMenuItems={({ row, closeMenu }) => [
          <MenuItem
            key="edit"
            onClick={() => {
              closeMenu();
              setEditingLeave(row.original);
              setOpenLeaveModal(true);
            }}
          >
            Edit
          </MenuItem>,
          <MenuItem
            key="delete"
            onClick={() => {
              closeMenu();
              setSelectedRecord(row.original);
              setOpenConfirm(true);
            }}
          >
            Delete
          </MenuItem>,
        ]}
        // ✅ Add Button inside the Table Toolbar (LEFT SIDE)
        renderTopToolbarCustomActions={() => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start", // 👈 left aligned
              width: "100%",
              ml: 1, // little margin for spacing
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setEditingLeave(null);
                setOpenLeaveModal(true);
              }}
            >
              Add Leave Type
            </Button>
          </Box>
        )}
      />

      {/* Confirm Delete Dialog */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this Leave Type?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Leave Modal */}
      <LeaveFormModal
        open={openLeaveModal}
        onClose={() => setOpenLeaveModal(false)}
        onSubmit={handleSubmitLeave}
        initialValues={editingLeave}
      />
    </Box>
  );
};
